import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import NewPasswordForm from "@/components/Auth/NewPasswordForm";
import Header from "@/components/HeaderUpdated";
import Footer from "@/components/Footer";

/**
 * Password-recovery landing page.
 *
 * Supabase appends the recovery token to the URL hash; the client
 * (detectSessionInUrl is on by default) consumes it and establishes a
 * recovery session, firing a PASSWORD_RECOVERY event. While that session is
 * active the user can set a new password via supabase.auth.updateUser(),
 * which is exactly what NewPasswordForm (-> AuthContext.updateUserPassword)
 * does. If there is no recovery session, the link is invalid/expired.
 *
 * This is independent of the DartsBeat integration — it only touches Supabase
 * auth (the local Supabase password, used by /admin/login).
 */
const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [hasSession, setHasSession] = useState<boolean | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setHasSession((prev) => prev || !!session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" || session) {
        setHasSession(true);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSuccess = async () => {
    // End the recovery session so the user signs in fresh with the new password.
    await supabase.auth.signOut();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-brand-darker flex flex-col">
      <Header hideAuthLinks={true} />

      <div className="flex-1 flex items-center justify-center p-4 py-16">
        <div className="w-full max-w-md">
          <Card className="bg-brand-gray border-none">
            {hasSession === false ? (
              <div className="p-8 text-center space-y-3">
                <h1 className="text-xl font-bold text-white">Link inválido ou expirado</h1>
                <p className="text-gray-400 text-sm">
                  Este link de recuperação já não é válido. Peça um novo a partir
                  do ecrã de login.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/admin/login")}
                  className="text-sm text-brand-red hover:underline"
                >
                  Voltar ao login
                </button>
              </div>
            ) : (
              <NewPasswordForm
                onSuccess={handleSuccess}
                onBack={() => navigate("/admin/login")}
              />
            )}
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ResetPassword;
